import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Steps,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    BugOutlined,
    DatabaseOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    HistoryOutlined,
    MessageOutlined,
    PlusOutlined,
    ReloadOutlined,
    RobotOutlined,
    RocketOutlined,
    SoundOutlined
} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {useNavigate} from 'react-router-dom';
import {agentConfigService} from '../../services/agentConfigService';
import {SkillConfig, skillConfigService} from '../../services/skillConfigService';
import GreetingConfigModal from './GreetingConfigModal';
import MemoryManageModal from './MemoryManageModal';
import AgentDebugModal from './AgentDebugModal';

const {Title, Text} = Typography;
const {TextArea} = Input;

interface AgentConfig {
    id?: number;
    name: string;
    /** 不可修改的唯一 code（系统生成或固定常量），用于触发/路由定位 */
    code?: string;
    systemPrompt?: string;
    agentSetting?: string;
    toolSetting?: string;
    outputFormat?: string;
    enableFinalOutput?: number;
    tools?: string;
    skills?: string;
    modelConfigId?: number;
    modelDisplayName?: string;
    boundModelType?: string;
    description?: string;
    executionMode?: string;
    agentScope?: string;
    executionPurpose?: string;
    maxTurns?: number;
    outputMode?: string;
    chatMode?: number;
    timeoutSeconds?: number;
    retryCount?: number;
    startingGunTools?: string;
    startingGunEnabled?: number;
    excludedSections?: string;
    isActive?: boolean;
    triggers?: TriggerConfig[];
    outputs?: OutputConfig[];
    createdAt?: string;
    updatedAt?: string;
    // agent-to-agent call 相关字段
    agentCallEnabled?: number;
    agentCallMaxDepth?: number;
    agentCallMaxParallel?: number;
    agentCallDefaultTimeoutSeconds?: number;
    agentCallCollectWaitSeconds?: number;
    // 父智能体挂起恢复配置
    agentCallResumeTimeoutSeconds?: number;
    agentCallResumeMaxRetries?: number;
    agentCallResumeOnPartialFailure?: number;
    // 任务规划
    taskPlanEnabled?: number;
    // Prompt Section 注入开关（默认关闭，开启后注入策略/风控/决策流程等默认段落）
    enablePromptSections?: number;
}

const EXCLUDED_SECTION_OPTIONS = [
    {value: 'STRATEGY', label: '策略 - 默认交易策略说明'},
    {value: 'RISK', label: '风控约束 - 风控规则及参数'},
    {value: 'DECISION_FLOW', label: '决策流程 - 5步决策步骤'},
    {value: 'ROLE', label: '角色定义 - Agent角色定位'},
    {value: 'FC_PROTOCOL', label: '交互协议 - FC/聊天模式规则'},
];

interface TriggerConfig {
    id?: number;
    agentConfigId?: number;
    triggerKind?: string;
    eventType: string;
    triggerSourceType?: 'EVENT' | 'MESSAGE';
    messageType?: string;
    priority?: number;
    description?: string;
    isActive?: number;
}

interface OutputConfig {
    id?: number;
    agentConfigId?: number;
    messageType: string;
    outputKind?: string;
    targetAgentConfigId?: number;
    description?: string;
    isActive?: number;
}

interface ToolInfo {
    name: string;
    label?: string;
    description: string;
    parameters?: string;
}

interface ModelInfo {
    configId: number;
    modelId: string;
    displayName: string;
    provider: string;
    modelType?: string;
    timeoutSeconds?: number;
    retryCount?: number;
}

interface AgentRelation {
    id?: number;
    agentId: number;
    subAgentId: number;
    relationType: string;
    priority?: number;
    callAlias?: string;
    delegationPrompt?: string;
    callEnabled?: number;
    allowNestedCalls?: number;
    maxDepthOverride?: number;
    timeoutSeconds?: number;
    maxParallelCalls?: number;
    isActive?: number;
}

interface SubAgentEntry {
    subAgentId: number;
    relationType: string;
    callAlias: string;
    delegationPrompt: string;
    callEnabled: number;
    allowNestedCalls: number;
    maxDepthOverride: number | null;
    timeoutSeconds: number | null;
    maxParallelCalls: number | null;
    priority: number;
    key: string;
}

const EVENT_TYPE_OPTIONS = [
    {value: 'RADAR_RESULT_READY', label: 'RADAR_RESULT_READY - 雷达结果'},
    {value: 'CONTRACT_TOP_N_READY', label: 'CONTRACT_TOP_N_READY - TopN'},
    {value: 'PATTERN_RESULT_READY', label: 'PATTERN_RESULT_READY - 形态识别'},
    {value: 'ZONE_RESULT_READY', label: 'ZONE_RESULT_READY - 区域识别'},
    {value: 'MANUAL_TRIGGER', label: 'MANUAL_TRIGGER - 手动触发'},
];

const MESSAGE_TYPE_OPTIONS = [
    {value: 'TRADE_PROPOSAL', label: 'TRADE_PROPOSAL - 交易建议'},
    {value: 'WATCH_SIGNAL', label: 'WATCH_SIGNAL - 观望信号'},
    {value: 'RISK_REVIEW_REQUEST', label: 'RISK_REVIEW_REQUEST - 风控请求'},
    {value: 'RISK_REVIEW_REPLY', label: 'RISK_REVIEW_REPLY - 风控回复'},
    {value: 'TRADE_EXECUTION_RESULT', label: 'TRADE_EXECUTION_RESULT - 交易结果'},
    {value: 'TRADE_CANCELLED', label: 'TRADE_CANCELLED - 交易取消'},
    {value: 'TRADE_ACTIONS', label: 'TRADE_ACTIONS - 交易动作'},
    {value: 'TREND_ANALYSIS', label: 'TREND_ANALYSIS - 趋势分析'},
    {value: 'MARKET_REGIME_RECOGNITION', label: 'MARKET_REGIME_RECOGNITION - 市场形态识别'},
    {value: 'FUNDAMENTAL_RISK_CONTROL', label: 'FUNDAMENTAL_RISK_CONTROL - 基本面风控'},
    {value: 'TRADE_PLAN', label: 'TRADE_PLAN - 交易规划'},
];

const MESSAGE_EVENT_TYPE_MAP: Record<string, string> = {
    TRADE_PROPOSAL: 'MESSAGE_TRADE_PROPOSAL_READY',
    WATCH_SIGNAL: 'MESSAGE_WATCH_SIGNAL_READY',
    RISK_REVIEW_REQUEST: 'MESSAGE_RISK_REVIEW_REQUEST_READY',
    RISK_REVIEW_REPLY: 'MESSAGE_RISK_REVIEW_REPLY_READY',
    TRADE_EXECUTION_RESULT: 'MESSAGE_TRADE_EXECUTION_RESULT_READY',
    TRADE_CANCELLED: 'MESSAGE_TRADE_CANCELLED_READY',
    TRADE_ACTIONS: 'MESSAGE_TRADE_ACTIONS_READY',
    TREND_ANALYSIS: 'MESSAGE_TREND_ANALYSIS_READY',
    MARKET_REGIME_RECOGNITION: 'MESSAGE_MARKET_REGIME_RECOGNITION_READY',
    FUNDAMENTAL_RISK_CONTROL: 'MESSAGE_FUNDAMENTAL_RISK_CONTROL_READY',
    TRADE_PLAN: 'MESSAGE_TRADE_PLAN_READY',
};

const EVENT_MESSAGE_TYPE_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(MESSAGE_EVENT_TYPE_MAP).map(([messageType, eventType]) => [eventType, messageType])
);

const EVENT_SAMPLE_JSON: Record<string, string> = {
    RADAR_RESULT_READY: JSON.stringify({
        eventType: "RADAR_RESULT_READY",
        cex: "OKX",
        exchange: "OKX",
        timeframe: "1H",
        cacheKey: "OKX_1H",
        generatedAt: "2026-06-08T15:30:00",
        globalLongRatio: 52.35,
        topLongCoins: [
            {symbol: "BTC", ratio: 68.5},
            {symbol: "ETH", ratio: 62.1},
            {symbol: "SOL", ratio: 59.8}
        ],
        topShortCoins: [
            {symbol: "ADA", ratio: 65.2},
            {symbol: "XRP", ratio: 61.3},
            {symbol: "DOGE", ratio: 58.7}
        ]
    }, null, 2),
    CONTRACT_TOP_N_READY: JSON.stringify({
        eventType: "CONTRACT_TOP_N_READY",
        cex: "OKX",
        vendor: "OKX",
        isLiveTrading: 1,
        topN: 30,
        sortBy: "volume",
        hourStr: "2026-06-08 15",
        topInstIds: [
            {rank: 1, instId: "BTC-USDT-SWAP"},
            {rank: 2, instId: "ETH-USDT-SWAP"},
            {rank: 3, instId: "SOL-USDT-SWAP"}
        ],
        totalCount: 30
    }, null, 2),
    PATTERN_RESULT_READY: JSON.stringify({
        eventType: "PATTERN_RESULT_READY",
        cex: "OKX",
        patternResultId: 12345,
        symbol: "BTC-USDT-SWAP",
        interval: "1H",
        patternCode: "DOJI",
        patternName: "十字星",
        direction: 1,
        confidence: 75.5,
        refPrice: 67250.30,
        klineEndTime: 1717804800000
    }, null, 2),
    ZONE_RESULT_READY: JSON.stringify({
        eventType: "ZONE_RESULT_READY",
        cex: "OKX",
        zoneResultId: 67890,
        symbol: "ETH-USDT-SWAP",
        interval: "4H",
        zoneType: "HIGH",
        patternCode: "RESISTANCE_ZONE",
        direction: -1,
        confidence: 82.0,
        zoneUpper: 3520.00,
        zoneLower: 3450.50,
        zonePrice: 3485.25
    }, null, 2),
    MANUAL_TRIGGER: JSON.stringify({
        eventType: "MANUAL_TRIGGER",
        triggeredBy: "user",
        instruction: "请分析BTC-USDT-SWAP当前走势",
        userId: 1001,
        apiKeyId: 2002
    }, null, 2),
};

const buildMessageTriggerSampleJson = (messageType: string) => JSON.stringify({
    eventType: MESSAGE_EVENT_TYPE_MAP[messageType],
    messageType,
    traceId: "trace-20260617-001",
    correlationId: "corr-20260617-001",
    fromRole: "MARKET_ANALYST",
    fromAgentId: 101,
    cex: "OKX",
    messagePayload: JSON.parse(OUTPUT_SAMPLE_JSON[messageType] || '{}')
}, null, 2);

const getResolvedTriggerEventType = (trigger: TriggerConfig): string => {
    if (getTriggerSourceType(trigger) === 'MESSAGE') {
        return trigger.messageType ? MESSAGE_EVENT_TYPE_MAP[trigger.messageType] || '' : '';
    }
    return trigger.eventType || '';
};

const getTriggerPreviewTitle = (trigger: TriggerConfig): string => {
    if (getTriggerSourceType(trigger) === 'MESSAGE') {
        return `${trigger.messageType || ''} - 消息桥接事件示例`;
    }
    return `${trigger.eventType || ''} - 事件示例`;
};

const getTriggerSourceType = (trigger: TriggerConfig): 'EVENT' | 'MESSAGE' => {
    if (trigger.triggerSourceType) {
        return trigger.triggerSourceType;
    }
    if (trigger.triggerKind) {
        return trigger.triggerKind as 'EVENT' | 'MESSAGE';
    }
    return trigger.eventType && EVENT_MESSAGE_TYPE_MAP[trigger.eventType] ? 'MESSAGE' : 'EVENT';
};

const normalizeTriggerForUi = (trigger: TriggerConfig): TriggerConfig => {
    const sourceType = getTriggerSourceType(trigger);
    if (sourceType === 'MESSAGE') {
        return {
            ...trigger,
            triggerSourceType: 'MESSAGE',
            messageType: trigger.messageType || EVENT_MESSAGE_TYPE_MAP[trigger.eventType] || '',
        };
    }
    return {
        ...trigger,
        triggerSourceType: 'EVENT',
        messageType: undefined,
    };
};

const STARTING_GUN_ALLOWED_TOOLS = [
    {value: 'get_kline', label: 'K线数据+技术指标'},
    {value: 'get_radar', label: '多空雷达'},
    {value: 'get_topN', label: '合约排行榜'},
    {value: 'get_long_short_ratio', label: '多空比'},
    {value: 'get_candle_patterns', label: 'K线形态'},
    {value: 'get_zone_patterns', label: '区域形态'},
    {value: 'get_user_exchange', label: '用户交易所查询'},
    {value: 'get_user_trade_config', label: '交易配置查询'},
    {value: 'get_position', label: '仓位查询'},
    {value: 'get_balance', label: '账户余额查询'},
];

const OUTPUT_SAMPLE_JSON: Record<string, string> = {
    TRADE_PROPOSAL: JSON.stringify({
        actions: [{
            decision: "BUY",
            instId: "BTC-USDT-SWAP",
            openClose: "OPEN",
            direction: "LONG",
            amount: 100,
            lever: 10,
            orderType: "MARKET",
            confidence: 85,
            cex_dex: "OKX",
            reasoning: "当前价格突破阻力位，成交量放大，MACD金叉确认...",
            stopLoss: 95000,
            takeProfit: 98000
        }]
    }, null, 2),
    WATCH_SIGNAL: JSON.stringify({
        actions: [{
            decision: "HOLD",
            cex_dex: "OKX",
            reasoning: "市场处于震荡区间，无明显趋势信号，建议观望等待方向确认..."
        }]
    }, null, 2),
    RISK_REVIEW_REQUEST: JSON.stringify({
        actions: [{
            decision: "BUY",
            instId: "BTC-USDT-SWAP",
            openClose: "OPEN",
            direction: "LONG",
            amount: 100,
            lever: 10,
            confidence: 85,
            cex_dex: "OKX",
            reasoning: "当前价格突破阻力位，请求风控审核..."
        }],
        capitalUtilization: {availableEquity: 1000, usedMargin: 200, utilizationRate: 0.2}
    }, null, 2),
    RISK_REVIEW_REPLY: JSON.stringify({
        actions: [{
            decision: "APPROVE",
            reviewComment: "仓位风险可控，杠杆合理，同意执行交易"
        }]
    }, null, 2),
    TRADE_EXECUTION_RESULT: JSON.stringify({
        orderId: "1234567890",
        instId: "BTC-USDT-SWAP",
        status: "filled"
    }, null, 2),
    TRADE_CANCELLED: JSON.stringify({
        actions: [{
            decision: "CANCEL_ORDER",
            instId: "BTC-USDT-SWAP",
            orderId: "123456789",
            cex_dex: "OKX",
            reasoning: "市场价格已偏离预期入场点超过2%，撤销待执行交易"
        }]
    }, null, 2),
    TRADE_ACTIONS: JSON.stringify({
        actions: [
            {
                decision: "SELL",
                instId: "BTC-USDT-SWAP",
                openClose: "CLOSE",
                quantity: 0.01,
                cex_dex: "OKX",
                confidence: 85,
                reasoning: "BTC跌破支撑位，平仓止损"
            },
            {
                decision: "BUY",
                instId: "ETH-USDT-SWAP",
                openClose: "OPEN",
                direction: "LONG",
                amount: 100,
                lever: 5,
                cex_dex: "OKX",
                confidence: 76,
                reasoning: "ETH突破阻力位"
            },
            {
                decision: "CANCEL_ORDER",
                instId: "SOL-USDT-SWAP",
                orderId: "123456",
                cex_dex: "OKX",
                confidence: 90,
                reasoning: "SOL限价挂单已过期，撤销"
            }
        ]
    }, null, 2),
    TREND_ANALYSIS: JSON.stringify({
        cex_dex: "OKX",
        instId: "ETH-USDT-SWAP",
        timestamp: 0,
        trend_direction: "bullish",
        trend_strength: "strong",
        volatility: "medium",
        volume_status: "expanding",
        momentum: "positive",
        summary: [
            "ETH处于明确的上升趋势中",
            "4H ADX>25趋势强度大",
            "成交量从高位回落"
        ]
    }, null, 2),
    MARKET_REGIME_RECOGNITION: JSON.stringify({
        market_regime: "strong_uptrend",
        confidence: 0.88,
        reason: [
            "EMA持续多头排列",
            "成交量增长",
            "价格不断创新高"
        ]
    }, null, 2),
    FUNDAMENTAL_RISK_CONTROL: JSON.stringify({
        cex_dex: "OKX",
        instId: "ETH-USDT-SWAP",
        timestamp: 0,
        risk_level: "medium",
        position_size: "30%",
        max_loss: "2%",
        reason: [
            "波动率适中"
        ]
    }, null, 2),
    TRADE_PLAN: JSON.stringify({
        reasoning: "BTC-USDT-SWAP 4H/日线空头趋势明确，4H出现看跌吞没形态（置信度100%），MACD空头排列发散。1H RSI极度超卖(9-19)，短期有反弹需求。建议等待反弹至63,200-63,400阻力区做空，止损63,800，目标62,200/61,700。风险收益比约1:2.2+，符合C3稳健型策略要求。",
        cex_dex: "okx",
        instId: "BTC-USDT-SWAP",
        strategy_type: "空头趋势跟随",
        current_price: 62770,
        support_levels: [62200, 61700, 61000],
        resistance_levels: [63200, 63600, 64000],
        atr_1h: 480,
        entry_zone: {min: 63200, max: 63400},
        stop_loss: 63800,
        take_profit_1: 62200,
        take_profit_2: 61700,
        risk_reward_ratio1: 2.08,
        risk_reward_ratio2: 3.33
    }, null, 2),
};

const AgentConfigPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [configs, setConfigs] = useState<AgentConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingConfig, setEditingConfig] = useState<AgentConfig | null>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const watchedModelConfigId = Form.useWatch('modelConfigId', form);
    const watchedEnablePromptSections = Form.useWatch('enablePromptSections', form);
    const [availableTools, setAvailableTools] = useState<ToolInfo[]>([]);
    const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
    const [availableSkills, setAvailableSkills] = useState<SkillConfig[]>([]);
    // Step 3 本地状态
    const [localTriggers, setLocalTriggers] = useState<TriggerConfig[]>([]);
    const [localOutputs, setLocalOutputs] = useState<OutputConfig[]>([]);
    const [previewModal, setPreviewModal] = useState<{ visible: boolean; title: string; json: string }>({
        visible: false, title: '', json: ''
    });
    const [greetingModal, setGreetingModal] = useState<{ visible: boolean; agentId: number; agentName: string }>({
        visible: false, agentId: 0, agentName: ''
    });
    const [memoryModal, setMemoryModal] = useState<{ visible: boolean; agentId: number; agentName: string }>({
        visible: false, agentId: 0, agentName: ''
    });
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
    const [startingGunSaving, setStartingGunSaving] = useState<number | null>(null);
    const [versionModal, setVersionModal] = useState<{ visible: boolean; agentId: number; agentName: string }>({
        visible: false, agentId: 0, agentName: ''
    });
    const [debugModal, setDebugModal] = useState<{ visible: boolean; agentId: number; agentName: string }>({
        visible: false, agentId: 0, agentName: ''
    });
    const [localSubAgents, setLocalSubAgents] = useState<SubAgentEntry[]>([]);

    const validateLocalTriggers = () => {
        for (const trigger of localTriggers) {
            if (getTriggerSourceType(trigger) === 'MESSAGE') {
                if (!trigger.messageType) {
                    throw new Error('消息触发必须选择消息类型');
                }
                if (!getResolvedTriggerEventType(trigger)) {
                    throw new Error(`消息类型 ${trigger.messageType} 缺少桥接事件映射`);
                }
                continue;
            }
            if (!trigger.eventType) {
                throw new Error('事件触发必须选择事件类型');
            }
        }
    };

    const toggleExpand = (id: number) => {
        setExpandedRowKeys(prev =>
            prev.includes(id) ? prev.filter(k => k !== id) : [id]
        );
    };

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const data = await agentConfigService.getAllAgentConfigs();
            setConfigs(data);
        } catch {
            messageApi.error('加载智能体配置失败');
        } finally {
            setLoading(false);
        }
    };

    const loadOptions = async () => {
        try {
            const [tools, models, skills] = await Promise.all([
                agentConfigService.getAvailableTools(),
                agentConfigService.getAvailableModels(),
                skillConfigService.getActiveSkillConfigs()
            ]);
            setAvailableTools(tools);
            setAvailableModels(models.map(m => ({
                configId: m.configId, modelId: m.modelId,
                displayName: m.modelName, provider: m.provider, modelType: m.modelType,
                timeoutSeconds: m.timeoutSeconds, retryCount: m.retryCount
            })));
            setAvailableSkills(skills);
        } catch {
            messageApi.error('加载可用选项失败');
        }
    };

    useEffect(() => {
        loadConfigs();
        loadOptions();
    }, []);

    // 有产出配置且未开启聊天模式时，FC终止工具保持启用
    useEffect(() => {
        if (localOutputs.length > 0) {
            const currentChatMode = form.getFieldValue('chatMode');
            if (1 != currentChatMode) {
                form.setFieldsValue({enableFinalOutput: true});
            }
        }
    }, [localOutputs.length]);

    const handleAdd = () => {
        setEditingConfig(null);
        setCurrentStep(0);
        setLocalTriggers([]);
        setLocalOutputs([]);
        setLocalSubAgents([]);
        form.resetFields();
        form.setFieldsValue({isActive: true, enableFinalOutput: true, agentScope: 'DEDICATED'});
        setModalVisible(true);
    };

    const handleEdit = async (config: AgentConfig) => {
        setEditingConfig(config);
        setCurrentStep(0);
        let tools: string[] = [];
        if (config.tools) {
            try {
                tools = JSON.parse(config.tools);
            } catch {
                tools = [];
            }
        }
        let skills: number[] = [];
        if (config.skills) {
            try {
                skills = JSON.parse(config.skills);
            } catch {
                skills = [];
            }
        }
        let excludedSections: string[] = [];
        if (config.excludedSections) {
            try {
                excludedSections = JSON.parse(config.excludedSections);
            } catch {
                excludedSections = [];
            }
        }
        let subAgents: number[] = [];
        let relationType = '';
        if (config.id) {
            try {
                const relations = await agentConfigService.getSubAgents(config.id);
                subAgents = relations.map((r: AgentRelation) => r.subAgentId);
                if (relations.length > 0) relationType = relations[0].relationType;
                setLocalSubAgents(relations.map((r: AgentRelation, idx: number) => ({
                    key: `sa_${idx}`,
                    subAgentId: r.subAgentId,
                    relationType: r.relationType || 'MASTER_SLAVE',
                    callAlias: r.callAlias || '',
                    delegationPrompt: r.delegationPrompt || '',
                    callEnabled: null != r.callEnabled ? r.callEnabled : 1,
                    allowNestedCalls: null != r.allowNestedCalls ? r.allowNestedCalls : 0,
                    maxDepthOverride: r.maxDepthOverride || null,
                    timeoutSeconds: r.timeoutSeconds || null,
                    maxParallelCalls: r.maxParallelCalls || null,
                    priority: null != r.priority ? r.priority : idx,
                })));
            } catch {
                setLocalSubAgents([]);
            }
        }
        // 加载触发和产出配置
        let triggers: TriggerConfig[] = config.triggers || [];
        let outputs: OutputConfig[] = config.outputs || [];
        if (config.id && (!config.triggers || !config.outputs)) {
            try {
                const [t, o] = await Promise.all([
                    agentConfigService.getAgentTriggers(config.id),
                    agentConfigService.getAgentOutputs(config.id)
                ]);
                triggers = t;
                outputs = o;
            } catch {
            }
        }
        setLocalTriggers(triggers.map(normalizeTriggerForUi));
        setLocalOutputs(outputs);
        form.resetFields();
        form.setFieldsValue({
            ...config, tools, skills, excludedSections, subAgents, relationType,
            // 显式设置以清除上一个编辑智能体的残留值(API @JsonInclude(NON_NULL)会排除null字段)
            timeoutSeconds: null != config.timeoutSeconds ? config.timeoutSeconds : null,
            retryCount: null != config.retryCount ? config.retryCount : null,
        });
        setModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            validateLocalTriggers();
            const submitData = {
                ...values,
                tools: JSON.stringify(values.tools || []),
                skills: JSON.stringify(values.skills || []),
                excludedSections: JSON.stringify(values.excludedSections || []),
                isActive: values.isActive ? 1 : 0,
                enableFinalOutput: values.enableFinalOutput ? 1 : 0,
                chatMode: values.chatMode ? 1 : 0,
                agentCallEnabled: values.agentCallEnabled ? 1 : 0,
                agentCallResumeOnPartialFailure: values.agentCallResumeOnPartialFailure ? 1 : 0,
                taskPlanEnabled: values.taskPlanEnabled ? 1 : 0,
                enablePromptSections: values.enablePromptSections ? 1 : 0,
                triggers: localTriggers.map(t => ({
                    triggerKind: t.triggerSourceType || 'EVENT',
                    eventType: t.triggerSourceType === 'EVENT' ? t.eventType : undefined,
                    messageType: t.triggerSourceType === 'MESSAGE' ? t.messageType : undefined,
                    priority: t.priority || 0,
                    description: t.description,
                    isActive: t.isActive ?? 1
                })),
                outputs: localOutputs.map(o => ({
                    messageType: o.messageType,
                    outputKind: o.outputKind || 'MESSAGE',
                    targetAgentConfigId: o.targetAgentConfigId,
                    description: o.description,
                    isActive: o.isActive ?? 1
                })),
            };

            let savedAgentId: number | undefined;
            if (editingConfig?.id) {
                await agentConfigService.updateAgentConfig(editingConfig.id, submitData);
                savedAgentId = editingConfig.id;
                messageApi.success('智能体配置更新成功');
            } else {
                const newConfig = await agentConfigService.createAgentConfig(submitData);
                savedAgentId = newConfig.id;
                messageApi.success('智能体配置创建成功');
            }
            // 保存子Agent关系
            if (savedAgentId) {
                const existingRelations = await agentConfigService.getSubAgents(savedAgentId);
                for (const r of existingRelations) await agentConfigService.deleteAgentRelation(r.id);
                for (const sa of localSubAgents) {
                    if (!sa.subAgentId) continue;
                    await agentConfigService.saveAgentRelation({
                        agentId: savedAgentId,
                        subAgentId: sa.subAgentId,
                        relationType: sa.relationType || 'MASTER_SLAVE',
                        callAlias: sa.callAlias || undefined,
                        delegationPrompt: sa.delegationPrompt || undefined,
                        callEnabled: sa.callEnabled,
                        allowNestedCalls: sa.allowNestedCalls,
                        maxDepthOverride: sa.maxDepthOverride || undefined,
                        timeoutSeconds: sa.timeoutSeconds || undefined,
                        maxParallelCalls: sa.maxParallelCalls || undefined,
                        priority: sa.priority,
                        isActive: 1,
                    });
                }
            }
            setModalVisible(false);
            loadConfigs();
        } catch (error: any) {
            if (error?.message) messageApi.error(error.message);
            else messageApi.error('保存失败');
        }
    };

    const handleDelete = async (config: AgentConfig) => {
        if (!config.id) return;
        try {
            await agentConfigService.deleteAgentConfig(config.id);
            messageApi.success('删除成功');
            loadConfigs();
        } catch (error: any) {
            messageApi.error(error.message || '删除失败');
        }
    };

    const handleGreetingConfig = (config: AgentConfig) => {
        setGreetingModal({visible: true, agentId: config.id || 0, agentName: config.name});
    };

    const handleMemoryManage = (config: AgentConfig) => {
        setMemoryModal({visible: true, agentId: config.id || 0, agentName: config.name});
    };

    const handleVersionHistory = (config: AgentConfig) => {
        setVersionModal({visible: true, agentId: config.id || 0, agentName: config.name});
    };

    const handleChat = (config: AgentConfig) => {
        window.dispatchEvent(new CustomEvent('agent-chat-start', {detail: {agentConfig: config}}));
    };

    const handleDebug = (config: AgentConfig) => {
        setDebugModal({visible: true, agentId: config.id || 0, agentName: config.name});
    };

    const handleToggleStatus = async (config: AgentConfig) => {
        if (!config.id) return;
        try {
            await agentConfigService.updateAgentConfig(config.id, {...config, isActive: config.isActive ? 0 : 1});
            messageApi.success(`已${config.isActive ? '禁用' : '启用'}`);
            loadConfigs();
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const handleSaveStartingGun = async (record: AgentConfig, enabled: boolean, tools: string[]) => {
        if (!record.id) return;
        setStartingGunSaving(record.id);
        try {
            await agentConfigService.updateAgentConfig(record.id, {
                ...record,
                startingGunEnabled: enabled ? 1 : 0,
                startingGunTools: JSON.stringify(tools),
            });
            messageApi.success('发令枪配置已保存');
            loadConfigs();
        } catch (error: any) {
            messageApi.error(error.message || '保存失败');
        } finally {
            setStartingGunSaving(null);
        }
    };

    const expandedRowRender = (record: AgentConfig) => {
        const initialTools: string[] = (() => {
            try {
                return record.startingGunTools ? JSON.parse(record.startingGunTools) : [];
            } catch {
                return [];
            }
        })();
        return (
            <AgentStartingGunForm
                record={record}
                initialTools={initialTools}
                saving={startingGunSaving}
                onSave={handleSaveStartingGun}
                availableTools={availableTools}
            />
        );
    };

    // Step 3 helpers
    const addTrigger = () => setLocalTriggers([...localTriggers, {
        eventType: '',
        triggerSourceType: 'EVENT',
        messageType: undefined,
        isActive: 1
    }]);
    const removeTrigger = (idx: number) => setLocalTriggers(localTriggers.filter((_, i) => i !== idx));
    const updateTrigger = (idx: number, field: string, value: any) => {
        const updated = [...localTriggers];
        const current = {...updated[idx]};
        if (field === 'triggerSourceType') {
            current.triggerSourceType = value;
            current.eventType = '';
            current.messageType = undefined;
        } else if (field === 'messageType') {
            current.messageType = value;
        } else {
            (current as any)[field] = value;
        }
        updated[idx] = current;
        setLocalTriggers(updated);
    };
    const addOutput = () => setLocalOutputs([...localOutputs, {messageType: '', outputKind: 'MESSAGE', isActive: 1}]);
    const removeOutput = (idx: number) => setLocalOutputs(localOutputs.filter((_, i) => i !== idx));
    const updateOutput = (idx: number, field: string, value: any) => {
        const updated = [...localOutputs];
        (updated[idx] as any)[field] = value;
        setLocalOutputs(updated);
    };
    const addSubAgent = () => setLocalSubAgents([...localSubAgents, {
        key: `sa_${Date.now()}_${localSubAgents.length}`,
        subAgentId: 0,
        relationType: 'MASTER_SLAVE',
        callAlias: '',
        delegationPrompt: '',
        callEnabled: 1,
        allowNestedCalls: 0,
        maxDepthOverride: null,
        timeoutSeconds: null,
        maxParallelCalls: null,
        priority: localSubAgents.length,
    }]);
    const removeSubAgent = (idx: number) => setLocalSubAgents(localSubAgents.filter((_, i) => i !== idx));
    const updateSubAgent = (idx: number, field: string, value: any) => {
        const updated = [...localSubAgents];
        (updated[idx] as any)[field] = value;
        setLocalSubAgents(updated);
    };

    // Steps
    const steps = [
        {title: '基本信息'},
        {title: '提示词与工具'},
        {title: '触发与产出'},
        {title: '高级设置'},
    ];

    const stepStyle = (step: number): React.CSSProperties => ({
        display: currentStep === step ? 'block' : 'none'
    });

    const allStepContent = () => (
        <>
            {/* Step 0: 基本信息 */}
            <div style={stepStyle(0)}>
                <Form.Item name="name" label="智能体名称" rules={[{required: true, message: '请输入'}]}>
                    <Input placeholder="智能体名称" maxLength={100}/>
                </Form.Item>
                {editingConfig?.code && (
                    <Form.Item label="Code（唯一标识，不可修改）">
                        <Input value={editingConfig.code} readOnly
                               style={{fontFamily: 'monospace', background: '#f5f5f5', color: '#666'}}/>
                    </Form.Item>
                )}
                <Form.Item name="description" label="描述">
                    <Input placeholder="描述" maxLength={500}/>
                </Form.Item>
                <Form.Item name="modelConfigId" label="模型" rules={[{required: true, message: '请选择模型'}]}>
                    <Select placeholder="选择模型" options={availableModels.map(m => ({
                        value: m.configId, label: `${m.displayName} (${m.modelType === 'LOCAL' ? '本地' : '远程'})`
                    }))}/>
                </Form.Item>
                <Form.Item name="maxTurns" label="最大对话轮数" tooltip="智能体FC循环的最大轮次，默认10">
                    <InputNumber min={1} max={50} placeholder="10" style={{width: '100%'}}/>
                </Form.Item>
                <Form.Item name="isActive" label="启用状态" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                </Form.Item>
            </div>

            {/* Step 1: 提示词与工具 */}
            <div style={stepStyle(1)}>
                <Form.Item name="systemPrompt" label="系统预设Prompt" tooltip="定义智能体的行为和角色">
                    <TextArea rows={6} placeholder="系统预设Prompt" maxLength={10000} showCount
                              autoSize={{minRows: 3, maxRows: 16}}/>
                </Form.Item>
                <Form.Item name="agentSetting" label="Agent设定" tooltip="智能体自身的行为约束和偏好">
                    <TextArea rows={2} placeholder="Agent设定" maxLength={5000} showCount/>
                </Form.Item>
                <Form.Item name="toolSetting" label="工具设定" tooltip="工具调用说明和使用规范">
                    <TextArea rows={2} placeholder="工具设定" maxLength={5000} showCount/>
                </Form.Item>
                <Form.Item name="outputFormat" label="输出格式" tooltip="响应输出格式要求（选填）">
                    <TextArea rows={2} placeholder="如：请以 JSON 格式返回..." maxLength={2000} showCount/>
                </Form.Item>
                <Form.Item name="tools" label="工具" tooltip="选择智能体可使用的工具">
                    <Select mode="multiple" placeholder="选择工具" options={availableTools.map(t => ({
                        value: t.name, label: t.label || t.name
                    }))}
                            tagRender={(props) => {
                                const tool = availableTools.find(t => t.name === props.value);
                                const displayName = tool?.label || tool?.name || props.value;
                                const tooltipContent = tool ? (
                                    <div style={{maxWidth: 420}}>
                                        <div style={{
                                            marginBottom: tool.parameters ? 8 : 0, fontWeight: 500,
                                            color: 'var(--pt-primary)', lineHeight: 1.5
                                        }}>
                                            {tool.description}
                                        </div>
                                        {tool.parameters && (() => {
                                            try {
                                                const schema = JSON.parse(tool.parameters);
                                                return (
                                                    <div>
                                                        <div style={{
                                                            fontSize: 11,
                                                            color: 'var(--pt-text-secondary)',
                                                            marginBottom: 4
                                                        }}>注册Schema:
                                                        </div>
                                                        <pre style={{
                                                            background: 'var(--pt-bg-panel)',
                                                            color: 'var(--pt-text-primary)',
                                                            padding: 10,
                                                            borderRadius: 6,
                                                            fontSize: 11,
                                                            fontFamily: 'IBM Plex Mono, monospace',
                                                            margin: 0,
                                                            maxHeight: 260,
                                                            overflow: 'auto',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid rgba(255,255,255,0.07)'
                                                        }}>
                                                            {JSON.stringify(schema, null, 2)}
                                                        </pre>
                                                    </div>
                                                );
                                            } catch {
                                                return null;
                                            }
                                        })()}
                                    </div>
                                ) : props.label;
                                return (
                                    <Tooltip title={tooltipContent}
                                             overlayStyle={{maxWidth: 460}}>
                                        <Tag closable={props.closable} onClose={props.onClose}
                                             style={{marginRight: 3}}>
                                            {displayName}
                                        </Tag>
                                    </Tooltip>
                                );
                            }}
                            optionRender={(option) => {
                                const tool = availableTools.find(t => t.name === option.value);
                                const displayName = tool?.label || tool?.name || option.label;
                                const tooltipNode = tool ? (
                                    <div style={{maxWidth: 420}}>
                                        <div style={{
                                            marginBottom: tool.parameters ? 8 : 0, fontWeight: 500,
                                            color: 'var(--pt-primary)', lineHeight: 1.5
                                        }}>
                                            {tool.description}
                                        </div>
                                        {tool.parameters && (() => {
                                            try {
                                                const schema = JSON.parse(tool.parameters);
                                                return (
                                                    <div>
                                                        <div style={{
                                                            fontSize: 11,
                                                            color: 'var(--pt-text-secondary)',
                                                            marginBottom: 4
                                                        }}>注册Schema:
                                                        </div>
                                                        <pre style={{
                                                            background: 'var(--pt-bg-panel)',
                                                            color: 'var(--pt-text-primary)',
                                                            padding: 8,
                                                            borderRadius: 6,
                                                            fontSize: 11,
                                                            fontFamily: 'IBM Plex Mono, monospace',
                                                            margin: 0,
                                                            maxHeight: 200,
                                                            overflow: 'auto',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid rgba(255,255,255,0.07)'
                                                        }}>
                                                            {JSON.stringify(schema, null, 2)}
                                                        </pre>
                                                    </div>
                                                );
                                            } catch {
                                                return null;
                                            }
                                        })()}
                                    </div>
                                ) : null;
                                return (
                                    <Tooltip title={tooltipNode} placement="right"
                                             overlayStyle={{maxWidth: 460}}>
                                        <span>{displayName}</span>
                                    </Tooltip>
                                );
                            }}
                    />
                </Form.Item>
                <Form.Item name="skills" label="技能" tooltip="选择智能体可使用的技能">
                    <Select mode="multiple" placeholder="选择技能" options={availableSkills.map(s => ({
                        value: s.id, label: s.name
                    }))}/>
                </Form.Item>
                <Form.Item name="executionPurpose" label="执行目的" tooltip="决定角色定位和可用工具范围">
                    <Select placeholder="通用模式（全功能）" allowClear options={[
                        {value: 'MARKET_ANALYSIS', label: '市场分析 - 仅分析K线，不可交易'},
                        {value: 'TRADE_PRE_DECISION', label: '交易预决策 - 分析+查账户，不可下单'},
                        {value: 'RISK_REVIEW', label: '风控审核 - 审核交易，不可操作'},
                        {value: 'TRADE_EXECUTION', label: '交易执行 - 执行已审核的交易'}
                    ]}/>
                </Form.Item>
                <div style={{
                    marginBottom: 16,
                    padding: '12px',
                    background: 'var(--pt-bg-panel)',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.07)'
                }}>
                    <Text strong style={{color: 'var(--pt-primary)'}}>智能体调用配置</Text>
                    <Text type="secondary" style={{marginLeft: 8, fontSize: 12}}>
                        配置本智能体是否可调用其他智能体
                    </Text>
                    <Row gutter={16} style={{marginTop: 12}}>
                        <Col span={8}>
                            <Form.Item name="agentCallEnabled" label="允许调用其他智能体" valuePropName="checked"
                                       tooltip="开启后，智能体运行时可使用 call_agent 工具调用配置的子Agent">
                                <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="agentCallMaxDepth" label="最大调用深度"
                                       tooltip="1 表示只允许 A->B，2 表示 A->B->C">
                                <InputNumber min={1} max={10} placeholder="1" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="agentCallMaxParallel" label="最大并发子调用数">
                                <InputNumber min={1} max={20} placeholder="5" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="agentCallDefaultTimeoutSeconds" label="子调用默认超时(秒)">
                                <InputNumber min={10} max={3600} placeholder="300" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="agentCallCollectWaitSeconds" label="回收等待上限(秒)">
                                <InputNumber min={1} max={60} placeholder="10" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} style={{marginTop: 8}}>
                        <Col span={8}>
                            <Form.Item name="agentCallResumeTimeoutSeconds" label="恢复等待超时(秒)"
                                       tooltip="子调用全部完成后等待父智能体恢复的超时时间">
                                <InputNumber min={30} max={3600} placeholder="600" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="agentCallResumeMaxRetries" label="恢复最大重试次数"
                                       tooltip="父智能体恢复失败后的最大重试次数">
                                <InputNumber min={0} max={10} placeholder="3" style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="agentCallResumeOnPartialFailure" label="部分失败允许恢复"
                                       valuePropName="checked"
                                       tooltip="子调用部分失败时是否仍然恢复父智能体继续执行">
                                <Switch checkedChildren="允许" unCheckedChildren="禁止"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* 任务规划配置 */}
                <div style={{
                    marginTop: 16,
                    padding: 16,
                    background: 'var(--pt-bg-panel)',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.07)'
                }}>
                    <Text strong style={{color: 'var(--pt-primary)'}}>任务规划配置</Text>
                    <Text type="secondary" style={{marginLeft: 8, fontSize: 12}}>
                        开启后，智能体首轮交互可输出任务规划，系统自动按序/并发调度
                    </Text>
                    <Row gutter={16} style={{marginTop: 12}}>
                        <Col span={8}>
                            <Form.Item name="taskPlanEnabled" label="启用任务规划" valuePropName="checked"
                                       tooltip="开启后，智能体在首轮交互时会调用 submit_task_plan 工具提交任务规划，系统将自动按待办项的并发/顺序关系调度执行">
                                <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} style={{marginTop: 12}}>
                        <Col span={8}>
                            <Form.Item name="enablePromptSections" label="启用Prompt Section" valuePropName="checked"
                                       tooltip="开启后注入系统默认的策略/风控/角色/决策流程/交互协议段落；用户自定义内容中含同名标题时自动跳过重复段。默认关闭，行为与历史完全一致">
                                <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                <Form.Item
                    name="excludedSections"
                    label="排除提示词段落"
                    dependencies={['enablePromptSections']}
                    style={{marginBottom: 0}}
                    tooltip="禁用系统提示词中的默认段落（全选时某些段落仍可能因执行目的自动保留）">
                    <Select mode="multiple" placeholder="包含全部段落" options={EXCLUDED_SECTION_OPTIONS}
                            disabled={!watchedEnablePromptSections}
                            tagRender={(props) => {
                                const opt = EXCLUDED_SECTION_OPTIONS.find(o => o.value === props.value);
                                return (
                                    <Tag closable={props.closable} onClose={props.onClose}
                                         style={{marginRight: 3}}>
                                        {opt?.label || props.value}
                                    </Tag>
                                );
                            }}
                            optionRender={(option) => {
                                const opt = EXCLUDED_SECTION_OPTIONS.find(o => o.value === option.value);
                                return (
                                    <Tooltip title={opt?.label || ''}>
                                        <span>{opt?.label || option.label}</span>
                                    </Tooltip>
                                );
                            }}
                    />
                </Form.Item>
            </div>

            {/* Step 2: 触发与产出 */}
            <div style={stepStyle(2)}>
                <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                    <Col><Text strong>触发配置</Text><Text type="secondary" style={{
                        marginLeft: 8,
                        fontSize: 12
                    }}>哪些事件或消息桥接事件会触发本智能体</Text></Col>
                    <Col><Button type="dashed" icon={<PlusOutlined/>} size="small"
                                 onClick={addTrigger}>添加</Button></Col>
                </Row>
                <Table dataSource={localTriggers} rowKey={(_, i) => i} pagination={false} size="small"
                       style={{marginBottom: 24}} locale={{emptyText: '暂未配置触发规则'}}>
                    <Table.Column title="触发来源" width={120} render={(_, r: TriggerConfig, i) =>
                        <Select
                            value={getTriggerSourceType(r)}
                            style={{width: '100%'}}
                            onChange={(v) => updateTrigger(i, 'triggerSourceType', v)}
                            options={[
                                {value: 'EVENT', label: '事件'},
                                {value: 'MESSAGE', label: '消息'},
                            ]}
                        />
                    }/>
                    <Table.Column title="触发类型" width={240} render={(_, r: TriggerConfig, i) =>
                        getTriggerSourceType(r) === 'MESSAGE' ? (
                            <Select
                                value={r.messageType || undefined}
                                placeholder="选择消息"
                                style={{width: '100%'}}
                                onChange={(v) => updateTrigger(i, 'messageType', v)}
                                options={MESSAGE_TYPE_OPTIONS}
                            />
                        ) : (
                            <Select
                                value={r.eventType || undefined}
                                placeholder="选择事件"
                                style={{width: '100%'}}
                                onChange={(v) => updateTrigger(i, 'eventType', v)}
                                options={EVENT_TYPE_OPTIONS}
                            />
                        )
                    }/>
                    <Table.Column title="描述" render={(_, r: TriggerConfig, i) =>
                        <Input value={r.description} placeholder="描述"
                               onChange={e => updateTrigger(i, 'description', e.target.value)}/>
                    }/>
                    <Table.Column title="操作" width={100} render={(_, r: TriggerConfig, i) =>
                        <Space size={0}>
                            <Button type="text" icon={<EyeOutlined/>} size="small"
                                    disabled={getTriggerSourceType(r) === 'MESSAGE' ? !r.messageType : !r.eventType}
                                    onClick={() => setPreviewModal({
                                        visible: true,
                                        title: getTriggerPreviewTitle(r),
                                        json: getTriggerSourceType(r) === 'MESSAGE'
                                            ? buildMessageTriggerSampleJson(r.messageType || '')
                                            : (EVENT_SAMPLE_JSON[r.eventType] || '{}')
                                    })}/>
                            <Button type="text" danger icon={<DeleteOutlined/>} size="small"
                                    onClick={() => removeTrigger(i)}/>
                        </Space>
                    }/>
                </Table>
                <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                    <Col><Text strong>产出配置</Text><Text type="secondary" style={{
                        marginLeft: 8,
                        fontSize: 12
                    }}>本智能体产出什么消息，下游将收到对应桥接事件通知</Text></Col>
                    <Col><Button type="dashed" icon={<PlusOutlined/>} size="small"
                                 onClick={addOutput}>添加</Button></Col>
                </Row>
                <Table dataSource={localOutputs} rowKey={(_, i) => i} pagination={false} size="small"
                       locale={{emptyText: '暂未配置产出消息'}}>
                    <Table.Column title="消息类型" width={200} render={(_, r: OutputConfig, i) =>
                        <Select value={r.messageType || undefined} placeholder="选择消息" style={{width: '100%'}}
                                onChange={(v) => updateOutput(i, 'messageType', v)} options={MESSAGE_TYPE_OPTIONS}/>
                    }/>
                    <Table.Column title="产出类型" width={100} render={(_, r: OutputConfig, i) =>
                        <Select value={r.outputKind || 'MESSAGE'} style={{width: '100%'}}
                                onChange={(v) => updateOutput(i, 'outputKind', v)} options={[
                            {value: 'MESSAGE', label: 'MESSAGE'},
                            {value: 'EVENT', label: 'EVENT'},
                            {value: 'ACTION', label: 'ACTION'},
                        ]}/>
                    }/>
                    <Table.Column title="路由目标" width={160} render={(_, r: OutputConfig, i) =>
                        <Select value={r.targetAgentConfigId} placeholder="(归档)" allowClear style={{width: '100%'}}
                                onChange={(v) => updateOutput(i, 'targetAgentConfigId', v)}
                                options={configs.filter(c => c.id !== editingConfig?.id).map(c => ({
                                    value: c.id,
                                    label: c.name
                                }))}/>
                    }/>
                    <Table.Column title="描述" render={(_, r: OutputConfig, i) =>
                        <Input value={r.description} placeholder="描述"
                               onChange={e => updateOutput(i, 'description', e.target.value)}/>
                    }/>
                    <Table.Column title="操作" width={100} render={(_, r: OutputConfig, i) =>
                        <Space size={0}>
                            <Button type="text" icon={<EyeOutlined/>} size="small"
                                    disabled={!r.messageType}
                                    onClick={() => setPreviewModal({
                                        visible: true,
                                        title: `${r.messageType || ''} - 消息示例`,
                                        json: OUTPUT_SAMPLE_JSON[r.messageType] || '{}'
                                    })}/>
                            <Button type="text" danger icon={<DeleteOutlined/>} size="small"
                                    onClick={() => removeOutput(i)}/>
                        </Space>
                    }/>
                </Table>
            </div>

            {/* Step 3: 高级设置 */}
            <div style={stepStyle(3)}>
                <Form.Item name="outputMode" label="产出模式" tooltip="控制产出消息是否进入工作流触发后续智能体">
                    <Select placeholder="ALL - 始终进入工作流" options={[
                        {value: 'ALL', label: 'ALL - 始终进入工作流'},
                        {value: 'WORKFLOW', label: 'WORKFLOW - 仅工作流触发时'},
                        {value: 'CHAT', label: 'CHAT - 仅Chat触发时进入工作流'}
                    ]}/>
                </Form.Item>
                <Form.Item name="executionMode" label="执行模式" tooltip="优先级：智能体 > AI交易 > 全局配置">
                    <Select placeholder="使用上级配置" allowClear options={[
                        {value: 'LIVE', label: '实盘模式 - 真实下单'},
                        {value: 'DRY_RUN', label: '模拟模式 - 不提交订单'}
                    ]}/>
                </Form.Item>
                <Form.Item name="agentScope" label="智能体范围" tooltip="共享=执行一次，专属=每用户执行">
                    <Select placeholder="选择范围" options={[
                        {value: 'SHARED', label: '共享 - 执行一次，不依赖用户数据'},
                        {value: 'DEDICATED', label: '专属 - 每个绑定用户独立执行'}
                    ]}/>
                </Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="timeoutSeconds" label="超时时间(秒)"
                                   tooltip="单次模型调用超时，不设置则使用模型默认值">
                            <InputNumber min={1} max={600} style={{width: '100%'}}
                                         placeholder={
                                             (() => {
                                                 if (watchedModelConfigId) {
                                                     const model = availableModels.find(m => m.configId === watchedModelConfigId);
                                                     if (null != model?.timeoutSeconds) {
                                                         return `模型默认(${model.timeoutSeconds}秒)`;
                                                     }
                                                 }
                                                 return '模型默认(120秒)';
                                             })()
                                         }
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="retryCount" label="重试次数"
                                   tooltip="调用失败重试次数，不设置则使用模型默认值(仅远端模型生效)">
                            <InputNumber min={0} max={10} style={{width: '100%'}}
                                         placeholder={
                                             (() => {
                                                 if (watchedModelConfigId) {
                                                     const model = availableModels.find(m => m.configId === watchedModelConfigId);
                                                     if (null != model?.retryCount) {
                                                         return `模型默认(${model.retryCount}次)`;
                                                     }
                                                 }
                                                 return '模型默认(3次)';
                                             })()
                                         }
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="chatMode" label="聊天模式" valuePropName="checked"
                                   tooltip="开启后，聊天场景下智能体可自由输出自然语言，同时仍可调用工具和 submit_final_output。关闭则保持纯 FC 模式。">
                            <Switch checkedChildren="开启" unCheckedChildren="关闭"/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="enableFinalOutput" label="FC终止工具" valuePropName="checked"
                                   tooltip={localOutputs.length > 0 && !form.getFieldValue('chatMode')
                                       ? "已配置产出消息且未开启聊天模式，FC终止工具强制启用（下游智能体依赖结构化输出）"
                                       : "启用后智能体必须调用submit_final_output终止FC循环。如需聊天场景下自由对话，请开启「聊天模式」。"}>
                            <Switch checkedChildren="启用" unCheckedChildren="禁用"
                                    disabled={localOutputs.length > 0 && !form.getFieldValue('chatMode')}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                    <Col><Text strong>可调用的子Agent</Text><Text type="secondary" style={{
                        marginLeft: 8,
                        fontSize: 12
                    }}>配置本智能体可调用的子智能体及调用参数</Text></Col>
                    <Col>
                        <Button type="dashed" icon={<PlusOutlined/>} size="small"
                                onClick={addSubAgent}>添加</Button>
                    </Col>
                </Row>
                <Table dataSource={localSubAgents} rowKey="key" pagination={false} size="small"
                       style={{marginBottom: 24}} locale={{emptyText: '暂未配置可调用的子Agent'}}>
                    <Table.Column title="子Agent" width={140} render={(_, r: SubAgentEntry, i) =>
                        <Select value={r.subAgentId || undefined} placeholder="选择子Agent"
                                style={{width: '100%'}} showSearch optionFilterProp="label"
                                onChange={(v) => updateSubAgent(i, 'subAgentId', v)}
                                options={configs.filter(c => c.id !== editingConfig?.id).map(a => ({
                                    value: a.id, label: a.name
                                }))}/>
                    }/>
                    <Table.Column title="调用别名" width={120} render={(_, r: SubAgentEntry, i) =>
                        <Input value={r.callAlias} placeholder="如market_analyst"
                               onChange={e => updateSubAgent(i, 'callAlias', e.target.value)}/>
                    }/>
                    <Table.Column title="委托说明" width={160} render={(_, r: SubAgentEntry, i) =>
                        <Input value={r.delegationPrompt} placeholder="子智能体职责说明"
                               onChange={e => updateSubAgent(i, 'delegationPrompt', e.target.value)}/>
                    }/>
                    <Table.Column title="允许调用" width={70} align="center" render={(_, r: SubAgentEntry, i) =>
                        <Switch size="small" checked={1 == r.callEnabled}
                                onChange={(v) => updateSubAgent(i, 'callEnabled', v ? 1 : 0)}/>
                    }/>
                    <Table.Column title="允许嵌套" width={70} align="center" render={(_, r: SubAgentEntry, i) =>
                        <Switch size="small" checked={1 == r.allowNestedCalls}
                                onChange={(v) => updateSubAgent(i, 'allowNestedCalls', v ? 1 : 0)}/>
                    }/>
                    <Table.Column title="深度覆盖" width={80} render={(_, r: SubAgentEntry, i) =>
                        <InputNumber value={r.maxDepthOverride} min={1} max={10} placeholder="继承"
                                     style={{width: '100%'}}
                                     onChange={(v) => updateSubAgent(i, 'maxDepthOverride', v)}/>
                    }/>
                    <Table.Column title="超时(秒)" width={80} render={(_, r: SubAgentEntry, i) =>
                        <InputNumber value={r.timeoutSeconds} min={10} max={3600} placeholder="默认"
                                     style={{width: '100%'}}
                                     onChange={(v) => updateSubAgent(i, 'timeoutSeconds', v)}/>
                    }/>
                    <Table.Column title="并发限制" width={80} render={(_, r: SubAgentEntry, i) =>
                        <InputNumber value={r.maxParallelCalls} min={1} max={20} placeholder="默认"
                                     style={{width: '100%'}}
                                     onChange={(v) => updateSubAgent(i, 'maxParallelCalls', v)}/>
                    }/>
                    <Table.Column title="操作" width={50} render={(_, r: SubAgentEntry, i) =>
                        <Button type="text" danger icon={<DeleteOutlined/>} size="small"
                                onClick={() => removeSubAgent(i)}/>
                    }/>
                </Table>
            </div>
        </>
    );

    const columns: ColumnsType<AgentConfig> = [
        {title: '名称', dataIndex: 'name', key: 'name', width: 150, render: (t) => <Text strong>{t}</Text>},
        {
            title: 'Code', dataIndex: 'code', key: 'code', width: 180, render: (t) => t
                ? <Tag color="purple" style={{fontFamily: 'monospace'}}>{t}</Tag>
                : <Text type="secondary">—</Text>
        },
        {
            title: '模型', dataIndex: 'modelConfigId', key: 'modelConfigId', width: 180,
            render: (id, record) => {
                if (!id) return <Text type="secondary">未绑定</Text>;
                if (record.modelDisplayName) {
                    return (
                        <Tag color="blue">
                            {record.modelDisplayName} ({record.boundModelType === 'LOCAL' ? '本地' : '远程'})
                        </Tag>
                    );
                }
                const m = availableModels.find(x => x.configId === id);
                return m ? <Tag color="blue">{m.displayName} ({m.modelType === 'LOCAL' ? '本地' : '远程'})</Tag> :
                    <Tag>ID:{id}</Tag>;
            }
        },
        {
            title: '状态', dataIndex: 'isActive', key: 'isActive', width: 80,
            render: (v, record) => <Switch checked={v} onChange={() => handleToggleStatus(record)} size="small"/>
        },
        {
            title: '操作', key: 'action', width: 220,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="发令枪配置">
                        <Button type={record.id && expandedRowKeys.includes(record.id) ? 'primary' : 'text'}
                                icon={<RocketOutlined/>}
                                onClick={() => record.id && toggleExpand(record.id)} size="small"/>
                    </Tooltip>
                    <Tooltip title="版本历史">
                        <Button type="text" icon={<HistoryOutlined/>}
                                onClick={() => handleVersionHistory(record)} size="small"/>
                    </Tooltip>
                    <Tooltip title="招呼语配置">
                        <Button type="text" icon={<SoundOutlined/>} onClick={() => handleGreetingConfig(record)}
                                size="small"/>
                    </Tooltip>
                    <Tooltip title="记忆绑定">
                        <Button type="text" icon={<DatabaseOutlined/>} onClick={() => handleMemoryManage(record)}
                                size="small"/>
                    </Tooltip>
                    <Tooltip title="查看校验记录">
                        <Button
                            type="text"
                            icon={<EyeOutlined/>}
                            onClick={() => navigate(`/system/agent-output-validations?agentName=${encodeURIComponent(record.name)}`)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="调试">
                        <Button type="text" icon={<BugOutlined/>} onClick={() => handleDebug(record)}
                                size="small"/>
                    </Tooltip>
                    <Button type="text" icon={<MessageOutlined/>} onClick={() => handleChat(record)} size="small"/>
                    <Button type="text" icon={<EditOutlined/>} onClick={() => handleEdit(record)} size="small"/>
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record)}
                                icon={<ExclamationCircleOutlined style={{color: 'red'}}/>}>
                        <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{padding: 24}}>
            <Row justify="space-between" align="middle" style={{marginBottom: 16}}>
                <Col><Title level={4} style={{margin: 0}}><RobotOutlined style={{marginRight: 8}}/></Title></Col>
                <Col>
                    <Space>
                        <Button icon={<ReloadOutlined/>} onClick={() => {
                            loadConfigs();
                            loadOptions();
                        }} loading={loading}/>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={handleAdd}/>
                    </Space>
                </Col>
            </Row>
            <Table columns={columns} dataSource={configs} rowKey="id" loading={loading} scroll={{x: 'max-content'}}
                   expandable={{
                       expandedRowRender,
                       expandedRowKeys,
                       onExpandedRowKeysChange: setExpandedRowKeys,
                       rowExpandable: (record) => !!record.id,
                   }}
                   pagination={{showSizeChanger: true, showQuickJumper: true, showTotal: (t) => `共 ${t} 个`}}/>

            <Modal title={editingConfig ? '编辑智能体' : '新增智能体'} open={modalVisible} width={800}
                   onCancel={() => setModalVisible(false)} footer={null}>
                <Steps current={currentStep} size="small" style={{marginBottom: 24}} items={steps}/>
                <Form form={form} layout="vertical" initialValues={{isActive: true}}>
                    {allStepContent()}
                </Form>
                <Row justify="space-between" style={{marginTop: 16}}>
                    <Col>
                        {currentStep > 0 && (
                            <Button onClick={() => setCurrentStep(currentStep - 1)}>上一步</Button>
                        )}
                    </Col>
                    <Col>
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>取消</Button>
                            {currentStep < steps.length - 1 ? (
                                <Button type="primary" onClick={async () => {
                                    try {
                                        const fields = currentStep === 0
                                            ? ['name', 'modelConfigId']
                                            : currentStep === 1
                                                ? [] : currentStep === 3 ? [] : [];
                                        if (fields.length > 0) await form.validateFields(fields);
                                        setCurrentStep(currentStep + 1);
                                    } catch {
                                    }
                                }}>下一步</Button>
                            ) : (
                                <Button type="primary" onClick={handleSave}>保存</Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Modal>

            <GreetingConfigModal
                visible={greetingModal.visible}
                agentId={greetingModal.agentId}
                agentName={greetingModal.agentName}
                onClose={() => setGreetingModal({visible: false, agentId: 0, agentName: ''})}
                onSaved={loadConfigs}
            />
            <MemoryManageModal
                visible={memoryModal.visible}
                agentId={memoryModal.agentId}
                agentName={memoryModal.agentName}
                onClose={() => setMemoryModal({visible: false, agentId: 0, agentName: ''})}
            />
            <VersionHistoryModal
                visible={versionModal.visible}
                agentId={versionModal.agentId}
                agentName={versionModal.agentName}
                onClose={() => setVersionModal({visible: false, agentId: 0, agentName: ''})}
                onRestored={loadConfigs}
            />

            <AgentDebugModal
                visible={debugModal.visible}
                agentId={debugModal.agentId}
                agentName={debugModal.agentName}
                onClose={() => setDebugModal({visible: false, agentId: 0, agentName: ''})}
            />

            <Modal title={previewModal.title} open={previewModal.visible} width={640}
                   onCancel={() => setPreviewModal({visible: false, title: '', json: ''})}
                   footer={<Button
                       onClick={() => setPreviewModal({visible: false, title: '', json: ''})}>关闭</Button>}>
                <pre style={{
                    background: 'var(--pt-bg-panel)', color: 'var(--pt-text-primary)', padding: 16, borderRadius: 8,
                    fontSize: 13, fontFamily: 'IBM Plex Mono, monospace', overflow: 'auto',
                    maxHeight: 480, border: '1px solid rgba(255,255,255,0.07)', whiteSpace: 'pre-wrap'
                }}>
                    {previewModal.json}
                </pre>
            </Modal>
        </div>
    );
};

interface StartingGunFormProps {
    record: AgentConfig;
    initialTools: string[];
    saving: number | null;
    onSave: (record: AgentConfig, enabled: boolean, tools: string[]) => void;
    availableTools: ToolInfo[];
}

const FC_TO_TOOL_NAME: Record<string, string> = {
    'get_kline': 'k_line',
    'get_position': 'position_info',
    'get_balance': 'balance_info',
    'get_radar': 'radar',
    'get_topN': 'top_n',
    'get_long_short_ratio': 'long_short_ratio',
    'get_candle_patterns': 'candle_patterns',
    'get_zone_patterns': 'zone_patterns',
    'get_user_exchange': 'user_exchange',
    'get_user_trade_config': 'user_trade_config',
};

const AgentStartingGunForm: React.FC<StartingGunFormProps> = ({
                                                                  record,
                                                                  initialTools,
                                                                  saving,
                                                                  onSave,
                                                                  availableTools
                                                              }) => {
    const [enabled, setEnabled] = useState(record.startingGunEnabled === 1);
    const [tools, setTools] = useState<string[]>(initialTools);

    const findToolByFcName = (fcName: string): ToolInfo | undefined => {
        const toolName = FC_TO_TOOL_NAME[fcName];
        return availableTools.find(t => t.name === (toolName || fcName));
    };

    return (
        <div style={{padding: '12px 24px'}}>
            <Space direction="vertical" style={{width: '100%'}} size="middle">
                <Row gutter={16} align="middle">
                    <Col>
                        <Text strong style={{marginRight: 8, fontSize: 13}}>发令枪</Text>
                        <Text type="secondary" style={{fontSize: 12}}>
                            智能体启动前自动执行工具获取上下文数据
                        </Text>
                    </Col>
                </Row>
                <Row gutter={16} align="middle">
                    <Col>
                        <Switch
                            checked={enabled}
                            onChange={(v) => setEnabled(v)}
                            checkedChildren="已启用"
                            unCheckedChildren="已禁用"
                        />
                    </Col>
                    <Col flex="auto">
                        <Select
                            mode="multiple"
                            value={tools}
                            onChange={(v) => setTools(v)}
                            placeholder="选择发令枪工具（执行顺序=选择顺序）"
                            options={STARTING_GUN_ALLOWED_TOOLS}
                            style={{width: '100%'}}
                            disabled={!enabled}
                            tagRender={(props) => {
                                const tool = findToolByFcName(props.value as string);
                                const displayName = STARTING_GUN_ALLOWED_TOOLS.find(o => o.value === props.value)?.label || props.value;
                                const tooltipContent = tool ? (
                                    <div style={{maxWidth: 420}}>
                                        <div style={{
                                            marginBottom: tool.parameters ? 8 : 0, fontWeight: 500,
                                            color: 'var(--pt-primary)', lineHeight: 1.5
                                        }}>
                                            {tool.description}
                                        </div>
                                        {tool.parameters && (() => {
                                            try {
                                                const schema = JSON.parse(tool.parameters);
                                                return (
                                                    <div>
                                                        <div style={{
                                                            fontSize: 11,
                                                            color: 'var(--pt-text-secondary)',
                                                            marginBottom: 4
                                                        }}>注册Schema:
                                                        </div>
                                                        <pre style={{
                                                            background: 'var(--pt-bg-panel)',
                                                            color: 'var(--pt-text-primary)',
                                                            padding: 10,
                                                            borderRadius: 6,
                                                            fontSize: 11,
                                                            fontFamily: 'IBM Plex Mono, monospace',
                                                            margin: 0,
                                                            maxHeight: 260,
                                                            overflow: 'auto',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid rgba(255,255,255,0.07)'
                                                        }}>
                                                            {JSON.stringify(schema, null, 2)}
                                                        </pre>
                                                    </div>
                                                );
                                            } catch {
                                                return null;
                                            }
                                        })()}
                                    </div>
                                ) : props.label;
                                return (
                                    <Tooltip title={tooltipContent}
                                             overlayStyle={{maxWidth: 460}}>
                                        <Tag closable={props.closable} onClose={props.onClose}
                                             style={{marginRight: 3}}>
                                            {displayName}
                                        </Tag>
                                    </Tooltip>
                                );
                            }}
                            optionRender={(option) => {
                                const tool = findToolByFcName(option.value as string);
                                const displayName = STARTING_GUN_ALLOWED_TOOLS.find(o => o.value === option.value)?.label || option.label;
                                const tooltipNode = tool ? (
                                    <div style={{maxWidth: 420}}>
                                        <div style={{
                                            marginBottom: tool.parameters ? 8 : 0, fontWeight: 500,
                                            color: 'var(--pt-primary)', lineHeight: 1.5
                                        }}>
                                            {tool.description}
                                        </div>
                                        {tool.parameters && (() => {
                                            try {
                                                const schema = JSON.parse(tool.parameters);
                                                return (
                                                    <div>
                                                        <div style={{
                                                            fontSize: 11,
                                                            color: 'var(--pt-text-secondary)',
                                                            marginBottom: 4
                                                        }}>注册Schema:
                                                        </div>
                                                        <pre style={{
                                                            background: 'var(--pt-bg-panel)',
                                                            color: 'var(--pt-text-primary)',
                                                            padding: 8,
                                                            borderRadius: 6,
                                                            fontSize: 11,
                                                            fontFamily: 'IBM Plex Mono, monospace',
                                                            margin: 0,
                                                            maxHeight: 200,
                                                            overflow: 'auto',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid rgba(255,255,255,0.07)'
                                                        }}>
                                                            {JSON.stringify(schema, null, 2)}
                                                        </pre>
                                                    </div>
                                                );
                                            } catch {
                                                return null;
                                            }
                                        })()}
                                    </div>
                                ) : null;
                                return (
                                    <Tooltip title={tooltipNode} placement="right"
                                             overlayStyle={{maxWidth: 460}}>
                                        <span>{displayName}</span>
                                    </Tooltip>
                                );
                            }}
                        />
                    </Col>
                    <Col>
                        <Button
                            type="primary" size="small"
                            loading={saving === record.id}
                            onClick={() => onSave(record, enabled, tools)}
                        >
                            保存
                        </Button>
                    </Col>
                </Row>
            </Space>
        </div>
    );
};

interface VersionHistoryItem {
    id: number;
    agentConfigId: number;
    versionNumber: number;
    changeDescription: string;
    createdAt: string;
}

interface VersionHistoryModalProps {
    visible: boolean;
    agentId: number;
    agentName: string;
    onClose: () => void;
    onRestored: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
                                                                     visible,
                                                                     agentId,
                                                                     agentName,
                                                                     onClose,
                                                                     onRestored
                                                                 }) => {
    const {message: messageApi} = App.useApp();
    const [versions, setVersions] = useState<VersionHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [restoringId, setRestoringId] = useState<number | null>(null);

    const loadVersions = async () => {
        setLoading(true);
        try {
            const data = await agentConfigService.getVersionHistory(agentId);
            setVersions(data);
        } catch {
            messageApi.error('加载版本历史失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && agentId > 0) {
            loadVersions();
        }
    }, [visible, agentId]);

    const handleRestore = async (versionId: number) => {
        setRestoringId(versionId);
        try {
            await agentConfigService.restoreVersion(agentId, versionId);
            messageApi.success('版本回滚成功');
            onRestored();
            onClose();
        } catch (error: any) {
            messageApi.error(error.message || '回滚失败');
        } finally {
            setRestoringId(null);
        }
    };

    return (
        <Modal title={`版本历史 - ${agentName}`} open={visible} width={640}
               onCancel={onClose} footer={null}>
            <Table dataSource={versions} rowKey="id" loading={loading} size="small"
                   locale={{emptyText: '暂无版本记录'}} pagination={{pageSize: 10, showTotal: (t) => `共 ${t} 条`}}>
                <Table.Column title="版本" dataIndex="versionNumber" width={60}
                              render={(v) => <Tag color="blue">v{v}</Tag>}/>
                <Table.Column title="变更说明" dataIndex="changeDescription" ellipsis/>
                <Table.Column title="时间" dataIndex="createdAt" width={160}
                              render={(v) => {
                                  if (!v) return '';
                                  const d = new Date(v);
                                  return d.toLocaleString('zh-CN');
                              }}/>
                <Table.Column title="操作" width={100} render={(_, r: VersionHistoryItem) => (
                    <Popconfirm
                        title={`确定回滚至版本 v${r.versionNumber}？`}
                        description="当前配置将被覆盖，不可撤销"
                        onConfirm={() => handleRestore(r.id)}
                        icon={<ExclamationCircleOutlined style={{color: 'orange'}}/>}
                    >
                        <Button type="link" size="small" loading={restoringId === r.id}
                                disabled={versions[0]?.id === r.id}>
                            回滚
                        </Button>
                    </Popconfirm>
                )}/>
            </Table>
        </Modal>
    );
};

export default AgentConfigPage;
